import { Button, Group, Paper, PasswordInput, Select, SimpleGrid, Stack, Switch, TextInput, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChangePassword, useUpdateMe, type UserPreferences } from '@/api/session';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { PageHeader } from '@/components/PageHeader';
import { useApiError } from '@/hooks/useApiError';
import { ApiKeysSection } from './ApiKeysSection';
import { CalendarIntegrationSection } from './CalendarIntegrationSection';
import { DesktopConsentSection } from './DesktopConsentSection';
import { KioskPinSection } from './KioskPinSection';
import { LocationConsentSection } from './LocationConsentSection';
import { NotificationSettingsSection } from './NotificationSettingsSection';
import { PrivacySection } from './PrivacySection';
import { SecuritySection } from './SecuritySection';
import { SessionsSection } from './SessionsSection';

const ZONES: string[] = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [
  'Europe/Berlin',
  'Europe/Vienna',
  'Europe/Zurich',
  'Europe/London',
  'UTC',
  'America/New_York',
];

/** Profil und Präferenzen (PROF-01/02). */
export function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useCurrentWorkspace();
  const update = useUpdateMe();
  const changePassword = useChangePassword();
  const onError = useApiError();
  const [name, setName] = useState(user.name);
  const [prefs, setPrefs] = useState<UserPreferences>(user.preferences);
  const [pw, setPw] = useState({ current: '', next: '' });
  useEffect(() => {
    setName(user.name);
    setPrefs(user.preferences);
  }, [user]);
  const set = <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => setPrefs((p) => ({ ...p, [key]: value }));

  return (
    <Stack maw={760}>
      <PageHeader title={t('profile.title')} />
      <Paper withBorder p="md">
        <Stack>
          <TextInput label={t('register.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} />
          <TextInput label={t('login.email')} value={user.email} readOnly />
        </Stack>
      </Paper>
      <Paper withBorder p="md">
        <Title order={5} mb="sm">
          {t('profile.preferences')}
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Select label={t('profile.language')} data={[{ value: 'de', label: 'Deutsch' }, { value: 'en', label: 'English' }]} value={prefs.locale} onChange={(v) => v && set('locale', v)} />
          <Select label={t('profile.timezone')} data={ZONES} value={prefs.timezone} onChange={(v) => v && set('timezone', v)} searchable />
          <Select label={t('profile.dateFormat')} data={['DD.MM.YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY']} value={prefs.dateFormat} onChange={(v) => v && set('dateFormat', v)} />
          <Select
            label={t('profile.timeFormat')}
            data={[{ value: 'H24', label: '24h (13:30)' }, { value: 'H12', label: '12h (1:30 PM)' }]}
            value={prefs.timeFormat}
            onChange={(v) => v && set('timeFormat', v as UserPreferences['timeFormat'])}
          />
          <Select
            label={t('profile.weekStart')}
            data={[
              { value: 'MONDAY', label: t('days.MONDAY') },
              { value: 'SUNDAY', label: t('days.SUNDAY') },
              { value: 'SATURDAY', label: t('days.SATURDAY') },
            ]}
            value={prefs.weekStart}
            onChange={(v) => v && set('weekStart', v as UserPreferences['weekStart'])}
          />
          <Select
            label={t('profile.durationFormat')}
            data={[
              { value: 'FULL', label: 'h:mm:ss' },
              { value: 'COMPACT', label: 'h:mm' },
              { value: 'DECIMAL', label: t('profile.decimal') },
            ]}
            value={prefs.durationFormat}
            onChange={(v) => v && set('durationFormat', v as UserPreferences['durationFormat'])}
          />
          <Select
            label={t('profile.theme')}
            data={[
              { value: 'SYSTEM', label: t('profile.themeSystem') },
              { value: 'LIGHT', label: t('profile.themeLight') },
              { value: 'DARK', label: t('profile.themeDark') },
            ]}
            value={prefs.theme}
            onChange={(v) => v && set('theme', v as UserPreferences['theme'])}
          />
          <TextInput label={t('profile.timesheetStart')} value={prefs.timesheetStartTime} onChange={(e) => set('timesheetStartTime', e.currentTarget.value)} />
        </SimpleGrid>
        <Switch mt="md" label={t('profile.groupSimilar')} checked={prefs.groupSimilarEntries} onChange={(e) => set('groupSimilarEntries', e.currentTarget.checked)} />
        <Group mt="md">
          <Button
            loading={update.isPending}
            onClick={() =>
              update.mutate(
                { name, preferences: prefs },
                { onSuccess: () => notifications.show({ color: 'green', message: t('common.saved') }), onError },
              )
            }
          >
            {t('common.save')}
          </Button>
        </Group>
      </Paper>
      {user.hasPassword && (
      <Paper withBorder p="md">
        <Title order={5} mb="sm">
          {t('profile.changePassword')}
        </Title>
        <Stack maw={360}>
          <PasswordInput label={t('profile.currentPassword')} value={pw.current} onChange={(e) => setPw({ ...pw, current: e.currentTarget.value })} />
          <PasswordInput label={t('profile.newPassword')} description={t('register.passwordHint')} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.currentTarget.value })} />
          <Button
            disabled={pw.next.length < 8}
            loading={changePassword.isPending}
            onClick={() =>
              changePassword.mutate(
                { currentPassword: pw.current, newPassword: pw.next },
                {
                  onSuccess: () => {
                    setPw({ current: '', next: '' });
                    notifications.show({ color: 'green', message: t('profile.passwordChanged') });
                  },
                  onError,
                },
              )
            }
          >
            {t('profile.changePassword')}
          </Button>
        </Stack>
      </Paper>
      )}
      <SecuritySection />
      <SessionsSection />
      <NotificationSettingsSection />
      <KioskPinSection />
      <LocationConsentSection />
      <DesktopConsentSection />
      <CalendarIntegrationSection />
      <ApiKeysSection />
      <PrivacySection />
    </Stack>
  );
}
